import { useState, useEffect, useMemo } from 'react';
import throttle from 'lodash/throttle';
import Bugsnag from '@bugsnag/browser';
import { errorify } from '@calefy-inc/utility';
import type { $TSFixMe } from '@calefy-inc/utilityTypes';

/**
 * Type for Google address autocomplete option
 */
export type GoogleAddressOption = {
  description: string;
  place_id: string;
  address?: string;
  city?: string;
  province?: string;
  postal?: string;
};

/**
 * Type for Google Maps address component (new Places API)
 */
type GoogleAddressComponent = {
  longText: string;
  shortText: string;
  types: Array<string>;
};

/**
 * Type guard to check if a value is a GoogleAddressOption
 * @param value - The value to check
 * @returns True if the value is a GoogleAddressOption
 */
export const isGoogleAddressOption = (
  value: unknown,
): value is GoogleAddressOption => {
  if (typeof value !== 'object' || value === null) {
    return false;
  }
  // Check for required properties using 'in' operator
  if (!('description' in value) || !('place_id' in value)) {
    return false;
  }
  // Use Object.getOwnPropertyDescriptor to safely access properties without type assertions
  const descriptionDesc = Object.getOwnPropertyDescriptor(value, 'description');
  const placeIdDesc = Object.getOwnPropertyDescriptor(value, 'place_id');
  return (
    descriptionDesc !== undefined &&
    placeIdDesc !== undefined &&
    typeof descriptionDesc.value === 'string' &&
    typeof placeIdDesc.value === 'string'
  );
};

/**
 * Normalize a Google Places address component.
 * The new Places API returns class instances with getter-based fields, so
 * Object.getOwnPropertyDescriptor cannot read longText/types directly.
 */
const normalizeAddressComponent = (
  value: unknown,
): GoogleAddressComponent | null => {
  if (typeof value !== 'object' || value === null) {
    return null;
  }

  const readComponent = (component: object): GoogleAddressComponent | null => {
    const record = component as Record<string, unknown>;
    const longText = record.longText;
    const shortText = record.shortText;
    const types = record.types;

    if (typeof longText !== 'string' || !Array.isArray(types)) {
      return null;
    }

    return {
      longText,
      shortText: typeof shortText === 'string' ? shortText : longText,
      types: types.filter((type): type is string => typeof type === 'string'),
    };
  };

  const directComponent = readComponent(value);
  if (directComponent) {
    return directComponent;
  }

  const wrappedValues = Object.values(value);
  if (wrappedValues.length === 1) {
    const wrappedValue = wrappedValues[0];
    if (typeof wrappedValue === 'object' && wrappedValue !== null) {
      return readComponent(wrappedValue);
    }
  }

  return null;
};

const normalizeAddressComponents = (
  components: unknown,
): Array<GoogleAddressComponent> => {
  if (!Array.isArray(components)) {
    return [];
  }

  return components
    .map(normalizeAddressComponent)
    .filter(
      (component): component is GoogleAddressComponent => component !== null,
    );
};

const findComponentWithType = (
  components: Array<GoogleAddressComponent>,
  type: string,
): GoogleAddressComponent | undefined => {
  return components.find((component) => component.types.includes(type));
};

const findLongNameWithType = (
  components: Array<GoogleAddressComponent>,
  type: string | null | undefined,
): string | undefined => {
  if (!type) {
    return undefined;
  }

  return findComponentWithType(components, type)?.longText;
};

const findShortNameWithType = (
  components: Array<GoogleAddressComponent>,
  type: string | null | undefined,
): string | undefined => {
  if (!type) {
    return undefined;
  }

  return findComponentWithType(components, type)?.shortText;
};

const findFirstLongNameWithTypes = (
  components: Array<GoogleAddressComponent>,
  types: Array<string>,
): string | undefined => {
  for (const type of types) {
    const name = findLongNameWithType(components, type);
    if (name) {
      return name;
    }
  }

  return undefined;
};

/**
 * Given a returned address object from Google Maps, extract the long name of the component that matches the particular address part that we're looking for (e.g. street name, city, &c.)
 * @param components - The Google Maps address components array
 * @param type - The type descriptor matching the address component that we're looking for
 * @return Either the relevant long name of the component or undefined (if it doesn't exist)
 */
export const extractNameWithType = (
  components: unknown,
  type: string | null | undefined,
): string | undefined => {
  if (!components || !type) {
    return undefined;
  }

  return findLongNameWithType(normalizeAddressComponents(components), type);
};

/**
 * Extracts address components from Google Maps address_components array
 * @param components - The address_components array from Google Maps
 * @return Object with address, city, province, and postal fields
 */
const extractAddressComponents = (components: unknown) => {
  if (!components) {
    return {
      address: undefined,
      city: undefined,
      province: undefined,
      postal: undefined,
    };
  }

  const normalizedComponents = normalizeAddressComponents(components);

  const streetNumber =
    findLongNameWithType(normalizedComponents, 'street_number') || '';
  const streetName = findLongNameWithType(normalizedComponents, 'route') || '';
  const streetAddress = `${streetNumber} ${streetName}`.trim() || undefined;

  const city = findFirstLongNameWithTypes(normalizedComponents, [
    'locality',
    'postal_town',
    'sublocality_level_1',
    'sublocality',
  ]);
  const province =
    findShortNameWithType(
      normalizedComponents,
      'administrative_area_level_1',
    ) ||
    findLongNameWithType(normalizedComponents, 'administrative_area_level_1') ||
    undefined;
  const postalCode =
    findLongNameWithType(normalizedComponents, 'postal_code') || undefined;

  return {
    address: streetAddress,
    city,
    province,
    postal: postalCode,
  };
};

/**
 * Hook to get Google address autocomplete options with full address details
 * @param {string} address - The address string to search for
 * @param {object} google - The Google Maps API object
 * @param {object} searchCircle - Optional Circle object to bias search results
 * @param {object} sessionToken - Optional session token for billing grouping
 * @return {Promise<Array>} - Promise that resolves to an array of options with description and address components
 */
const getGoogleAddressOptions = async (
  address: string,
  placesLibrary: $TSFixMe,
  searchCircle?: $TSFixMe,
  sessionToken?: $TSFixMe,
): Promise<Array<GoogleAddressOption>> => {
  if (!address || !placesLibrary?.AutocompleteSuggestion) {
    return [];
  }

  try {
    const request: $TSFixMe = {
      input: address,
      locationBias: searchCircle ? searchCircle.getBounds() : undefined,
    };
    if (sessionToken) {
      request.sessionToken = sessionToken;
    }

    const { suggestions } =
      await placesLibrary.AutocompleteSuggestion.fetchAutocompleteSuggestions(
        request,
      );

    if (!suggestions || suggestions.length === 0) {
      return [];
    }

    const optionsPromises = suggestions.map(async (suggestion: $TSFixMe) => {
      const placePrediction = suggestion.placePrediction;
      const place = placePrediction.toPlace();
      try {
        await place.fetchFields({ fields: ['addressComponents'] });
        const components = extractAddressComponents(place.addressComponents);
        return {
          description: placePrediction.text.text,
          place_id: placePrediction.placeId,
          ...components,
        };
      } catch {
        return {
          description: placePrediction.text.text,
          place_id: placePrediction.placeId,
        };
      }
    });

    const options = await Promise.all(optionsPromises);
    return options.filter(isGoogleAddressOption);
  } catch (e) {
    console.error('Error in getGoogleAddressOptions:', e);
    Bugsnag.notify(errorify(e));
    return [];
  }
};

/**
 * Hook to get Google address autocomplete options
 * @param address - The address string to search for
 * @param google - The Google Maps API object from useGoogle
 * @return - Object containing options array and centreSearchResults function
 */
export const useGoogleAddressOptions = (
  address: string,
  google: $TSFixMe | undefined,
  placesLibrary: $TSFixMe | undefined,
) => {
  const [options, setOptions] = useState<Array<GoogleAddressOption>>([]);
  const [searchCircle, setSearchCircle] = useState<$TSFixMe>(); // location to bias the results to; this can be set to a default location
  const [sessionToken, setSessionToken] = useState<$TSFixMe>(); //holds the session token to group together the details call with the autocomplete calls; this is mostly a billing feature

  const fetchOptions = useMemo(
    () =>
      throttle(async (address: string) => {
        try {
          const options = await getGoogleAddressOptions(
            address,
            placesLibrary,
            searchCircle,
            sessionToken,
          );
          return options;
        } catch (e) {
          console.error('Error in fetchOptions', e);
          Bugsnag.notify(errorify(e));
          return [];
        }
      }, 200),
    [placesLibrary, searchCircle, sessionToken],
  );

  // update the autocomplete suggestions whenever the address changes
  useEffect(() => {
    let active = true;

    const loadOptions = async () => {
      if (address === undefined || address === null || address === '') {
        setOptions([]);
        return;
      }

      if (!placesLibrary) {
        return;
      }

      try {
        const fetchedOptions = await fetchOptions(address);
        if (active) {
          setOptions(fetchedOptions ?? []);
        }
      } catch (error) {
        console.error('Error in address options effect:', error);
      }
    };

    void loadOptions();

    return () => {
      active = false;
    };
  }, [address, fetchOptions, placesLibrary]);

  // bias the user's results to their current location, if possible
  const centreSearchResults = () => {
    try {
      if (!google || !google.maps || !google.maps.Circle) {
        return;
      }
      //they've already granted permission and set a search circle
      if (searchCircle) {
        return;
      }
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            const geolocation = {
              lat: position.coords.latitude,
              lng: position.coords.longitude,
            };
            const circle = new google.maps.Circle({
              center: geolocation,
              radius: position.coords.accuracy,
            });
            setSearchCircle(circle);
          },
          (_error: $TSFixMe) => {
            setSearchCircle(
              new google.maps.Circle({
                center: {
                  lat: 51,
                  lng: -114,
                },
                radius: 10000,
              }),
            );
          },
        );
      } else {
        setSearchCircle(
          new google.maps.Circle({
            center: {
              lat: 51,
              lng: -114,
            },
            radius: 10000,
          }),
        );
      }
    } catch (e) {
      console.error(`Error centring search results: ${e}`);
      Bugsnag.notify(errorify(e));
    }
  };

  // centre the search results
  useEffect(() => {
    centreSearchResults();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [google]);

  // get a new session token once we can
  useEffect(() => {
    if (placesLibrary?.AutocompleteSessionToken) {
      const newSessionToken = new placesLibrary.AutocompleteSessionToken();
      setSessionToken(newSessionToken);
    }
  }, [placesLibrary]);

  return {
    options,
    centreSearchResults,
    sessionToken,
    setSessionToken,
  };
};
